import { WldConsentResponseStatus, WldDocumentSharingConsent, WldTermsContent } from '@interfaces';
import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import { DocumentSlice } from '@interfaces/models/doc-types';
import { Action, createReducer, on } from '@ngrx/store';
import { ClearData } from '@shared/models/prospect-data-models.interface';

import * as DocumentUploadActions from '../../actions/documents/documents.actions';
import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import * as TriageActions from '../../actions/triage/triage.actions';

export interface IFileMetaData {
  fileName: string;
  objectURL: string;
  s3BucketFileName?: string;
}

export interface IDocumentUploadState {
  filesMetaData: IFileMetaData[];
  clearData: ClearData;
  accountTypes?: string[];
  hasAcceptedWldTerms: boolean;
  termsContent: WldTermsContent | null;
  termsLoading: boolean;
  termsError: string | null;
  termsAccepting: boolean;
  termsAcceptError: string | null;
  consentStatus: WldDocumentSharingConsent | null;
  consentStatusError: string | null;
  wldStandardDocuments: UploadedFileEntry[];
  wldLetterDocuments: UploadedFileEntry[];
  wldTaxDocuments: UploadedFileEntry[];
}

export const initialState: IDocumentUploadState = {
  filesMetaData: [],
  clearData: {
    clearOutcome: '',
    uploadedFilename: '',
  },
  hasAcceptedWldTerms: false,
  termsContent: null,
  termsLoading: false,
  termsError: null,
  termsAccepting: false,
  termsAcceptError: null,
  consentStatus: null,
  consentStatusError: null,
  wldStandardDocuments: [],
  wldLetterDocuments: [],
  wldTaxDocuments: [],
};

const SLICE_TO_KEY: Record<
  'standard' | 'letter' | 'tax',
  'wldStandardDocuments' | 'wldLetterDocuments' | 'wldTaxDocuments'
> = {
  standard: 'wldStandardDocuments',
  letter: 'wldLetterDocuments',
  tax: 'wldTaxDocuments',
};

const documentUploadReducer = createReducer(
  initialState,
  on(DocumentUploadActions.updateFilesMetaData, (state, props) => {
    return {
      ...state,
      filesMetaData: props.payload,
    };
  }),
  on(DocumentUploadActions.updateProspectClearDocuments, (state, props) => ({
    ...state,
    clearData: props.payload,
  })),
  on(DocumentUploadActions.updateSelectedAccountTypes, (state, props) => ({
    ...state,
    accountTypes: props.payload,
  })),
  on(DocumentUploadActions.loadWldTermsContent, (state) => ({
    ...state,
    termsLoading: true,
  })),
  on(DocumentUploadActions.loadWldTermsContentSuccess, (state, props) => ({
    ...state,
    termsContent: props.payload,
    termsLoading: false,
    termsError: null,
  })),
  on(DocumentUploadActions.loadWldTermsContentFailure, (state, props) => ({
    ...state,
    termsError: props.error,
    termsLoading: false,
  })),

  on(DocumentUploadActions.acceptWldTerms, (state) => ({
    ...state,
    termsAccepting: true,
    termsAcceptError: null,
  })),
  on(DocumentUploadActions.acceptWldTermsSuccess, (state, { payload }) => ({
    ...state,
    // Mark accepted ONLY after the POST /document-sharing-consent succeeds.
    hasAcceptedWldTerms: true,
    termsAccepting: false,
    termsAcceptError: null,
    consentStatus: payload ?? state.consentStatus,
    consentStatusError: null,
  })),
  on(DocumentUploadActions.acceptWldTermsFailure, (state, { error }) => ({
    ...state,
    hasAcceptedWldTerms: false,
    termsAccepting: false,
    termsAcceptError: error,
  })),
  // Clear any stale error when a fresh consent-status request starts, otherwise a
  // single transient failure would permanently gate all future loadWldConsentStatus
  // dispatches for the session (selectWldConsentChecked = !!status || !!error).
  on(DocumentUploadActions.loadWldConsentStatus, (state) => ({
    ...state,
    consentStatusError: null,
  })),
  on(DocumentUploadActions.loadWldConsentStatusSuccess, (state, { payload }) => ({
    ...state,
    consentStatus: payload,
    consentStatusError: null,
    hasAcceptedWldTerms:
      payload.responseStatus === WldConsentResponseStatus.ACCEPT ? true : state.hasAcceptedWldTerms,
  })),
  on(DocumentUploadActions.loadWldConsentStatusFailure, (state, { error }) => ({
    ...state,
    consentStatusError: error,
  })),
  on(DocumentUploadActions.addWldDocument, (state, { slice, document }) => {
    const key = SLICE_TO_KEY[slice];
    // Guard against a rehydrated (sessionStorage) state that predates this field.
    const currentDocuments = state[key] ?? [];
    return {
      ...state,
      [key]: currentDocuments.some((doc) => doc.documentId === document.documentId)
        ? currentDocuments
        : [...currentDocuments, document],
    };
  }),
  on(DocumentUploadActions.removeWldDocument, (state, { slice, documentId }) => {
    const key = SLICE_TO_KEY[slice];
    return {
      ...state,
      [key]: (state[key] ?? []).filter((doc) => doc.documentId !== documentId),
    };
  }),
  on(DocumentUploadActions.clearWldDocuments, (state, { slice }) => {
    if (slice) {
      return { ...state, [SLICE_TO_KEY[slice]]: [] };
    }
    return {
      ...state,
      wldStandardDocuments: [],
      wldLetterDocuments: [],
      wldTaxDocuments: [],
    };
  }),

  on(TriageActions.ClearTriageState, TriageActions.OverviewContinue, (state) => ({
    ...state,
    wldStandardDocuments: [],
    wldLetterDocuments: [],
    wldTaxDocuments: [],
  })),
  on(DocumentUploadActions.clearDocumentUpload, () => ({ ...initialState })),
  on(StartOverStateCleared, () => ({ ...initialState })),
  on(DocumentUploadActions.setS3BucketFileNames, (state, props) => ({
    ...state,
    filesMetaData: state.filesMetaData.map((file) => {
      const match = props.payload.find((p) => p.objectURL === file.objectURL);
      return match ? { ...file, s3BucketFileName: match.s3BucketFileName } : file;
    }),
  })),
  on(DocumentUploadActions.rehydrateFilesFromS3, (state, props) => {
    if (props.slice && props.slice !== DocumentSlice.Document) {
      return state;
    }
    return {
      ...state,
      filesMetaData: state.filesMetaData.map((file) => {
        // S3 keys are lowercased on write by uploadFile, but the extension case in a stored
        // key (e.g. from a mixed-case original upload) can differ from what the middleware
        // echoes back, so compare case-insensitively. Both sides are guarded truthy first —
        // otherwise two keyless entries (undefined === undefined) would wrongly match.
        const match = props.payload.find(
          (p) =>
            !!p.s3BucketFileName &&
            !!file.s3BucketFileName &&
            p.s3BucketFileName.toLowerCase() === file.s3BucketFileName.toLowerCase(),
        );
        return match ? { ...file, objectURL: match.objectURL } : file;
      }),
    };
  }),
);

export function reducer(state: IDocumentUploadState | undefined, action: Action) {
  return documentUploadReducer(state, action);
}

export const getHasClientUploadedFile = (state: IDocumentUploadState) =>
  state.filesMetaData.length > 0;
